import { useQuery } from '@apollo/client';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import RoutePath from 'App/RoutePath';
import SectionError from 'Shared/SectionError';
import GenericExploreContent from '../../../Explore/GenericExploreContent/GenericExploreContent';
import HomeSectionSkeleton from '../components/HomeSectionSkeleton';
import { useAssetToken } from '../hooks/useAssetToken';
import { useSectionErrorLogging } from '../hooks/useSectionErrorLogging';
import { HOME_SECTION_QUERY_OPTIONS } from '../sectionQueryOptions';
import { HomeYourCourses } from './__generated__/HomeYourCourses';
import { HOME_YOUR_COURSES_QUERY } from './homeYourCoursesQueries';

/**
 * Courses the user has in progress. Single-day courses are excluded — they are
 * surfaced as Daily Boost and Tools For Your Day instead.
 */
const YourCourses = () => {
  const { t: translate } = useTranslation('homepage');
  const { assetToken } = useAssetToken();

  const { data, loading, error, refetch } = useQuery<HomeYourCourses>(
    HOME_YOUR_COURSES_QUERY,
    HOME_SECTION_QUERY_OPTIONS,
  );

  useSectionErrorLogging(error, data, 'your-courses');

  if (loading && !data) {
    return <HomeSectionSkeleton testId="your-courses-skeleton" tileCount={3} />;
  }

  if (error && !data) {
    // `errorPolicy: 'none'` makes refetch() reject on failure; the hook's own
    // `data` and `error` still drive what renders.
    return (
      <SectionError onRetry={async () => refetch().catch(() => undefined)} />
    );
  }

  const courses = (data?.user?.coursesInProgress ?? []).filter(
    course => course.totalDays !== 1,
  );

  if (!courses.length) {
    return null;
  }

  const viewAll = courses.length > 3;

  return (
    <section>
      <div className="home-section-heading">
        <h2 className="title-s section-title">
          {translate('your_courses_section.title')}
        </h2>
        {viewAll && (
          <Link
            className="link"
            to={RoutePath.YourCourses}
            aria-label={translate(
              'your_courses_section.arrow_link_button.text',
            )}
          >
            {translate('your_courses_section.arrow_link_button.text')}
          </Link>
        )}
      </div>
      <GenericExploreContent
        content={courses}
        assetToken={assetToken}
        source="explore-your-courses"
      />
    </section>
  );
};

export default YourCourses;
